import { Card, CardContent, Typography, Box, IconButton, Chip } from '@mui/material';
import {
  VisibilityOutlined as VisibilityOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { formatDate } from '@core/utils/formatting';
import type { FieldTicketResponse } from '@core/types/ticket';

interface TicketCardProps {
  ticket: FieldTicketResponse;
  onView: (ticketId: string) => void;
  onEdit: (ticketId: string) => void;
}

export function TicketCard({ ticket, onView, onEdit }: TicketCardProps) {
  const defectCount = ticket.defect_lines?.length || 0;
  const imageCount = ticket.defect_lines?.reduce(
    (total, line) => total + (line.images?.length || 0),
    0
  ) || 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft':
        return 'default';
      case 'Submitted':
      case 'Under Review':
        return 'success';
      case 'Approved':
        return 'primary';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        '&:active': {
          transform: 'scale(0.99)',
        },
        transition: 'transform 0.1s ease',
      }}
    >
      <CardContent sx={{ px: 4, py: 3 }}>
        {/* Header row: title + status chip */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {ticket.ticket_id_external || ticket.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(ticket.reported_date)}
            </Typography>
          </Box>
          <Chip
            label={ticket.status}
            color={getStatusColor(ticket.status)}
            size="small"
          />
        </Box>

        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 6, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Số lỗi
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {defectCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Số hình ảnh
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {imageCount}
            </Typography>
          </Box>
        </Box>

        {/* Action buttons — icon buttons for compact mobile layout */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={() => onView(ticket.ticket_id_external)}
            sx={{ color: 'primary.main' }}
            title="Xem chi tiết"
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onEdit(ticket.ticket_id_external)}
            sx={{ color: 'info.main' }}
            title="Chỉnh sửa"
          >
            <EditOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}
